/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { forwardRef, FC } from 'react';
import Select, { Props, Theme } from 'react-select';
import { useField } from 'formik';
import { colors } from '@airwallex/global';
import { Body } from '@airwallex/typography';
import { labelContainerCss, labelCss, errorCss } from '@/components/form/input/styles';

const { ultraViolet, ultraViolets, softGrey, greyOnGreyText, greys, inputBackground, red } = colors;

export interface SelectProps extends Props {
  name: string;
  optional?: boolean;
  label?: string;
  optionalDisplayText?: React.ReactNode;
  errorComponent?: React.ReactNode;
  className?: string;
}

export const customStyles = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: 14,
  }),
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  option: (provided: any) => ({
    ...provided,
    fontSize: 14,
  }),
};

const Index: FC<SelectProps> = forwardRef<HTMLSelectElement, SelectProps>(
  ({ name, label, optional, optionalDisplayText, errorComponent, className, styles = {}, ...rest }, ref) => {
    const [field, meta, helper] = useField(name);
    const selectCustomProps = {
      inputRef: ref,
      theme: (theme: Theme) => ({
        ...theme,
        borderRadius: 4,
        fontSize: 14,
        colors: {
          ...theme?.colors,
          primary25: ultraViolets[10],
          primary: ultraViolet,
          neutral20: softGrey,
          neutral30: greyOnGreyText,
          neutral80: greys[100],
        },
      }),
      ...rest,
      name: field.name,
    };
    const controlStyle = (provided: any) => ({
      ...provided,
      minHeight: 40,
      background: inputBackground,
      border: `1px solid ${meta.touched && meta.error ? red : softGrey}`,
    });

    // Have tested for 500 option items, the below logic would not bring performance issue.
    const value = ((selectCustomProps.options as { label: string; value: string }[]) ?? []).find(
      (option) => option.value === field.value
    );

    return (
      <div
        className={className}
        css={css`
          width: 130px;
        `}
      >
        <div css={labelContainerCss}>
          {label && (
            <label htmlFor={name} css={labelCss}>
              <Body>{label}</Body>
              {optional && <Body variant="100.Disabled">{optionalDisplayText || 'Optional'}</Body>}
            </label>
          )}
        </div>
        <Select
          {...selectCustomProps}
          styles={{
            ...customStyles,
            control: controlStyle,
            ...styles,
          }}
          className={`formikSelect ${name}`}
          onChange={(option: any) => {
            if (option?.value) {
              helper.setValue(option.value);
            } else if (Array.isArray(option)) {
              helper.setValue((option as unknown[] as { value: string }[]).map(({ value: itemValue }) => itemValue));
            }
          }}
          value={value}
        />
        {meta.touched && meta.error ? <div css={errorCss}>{errorComponent || meta.error}</div> : null}
      </div>
    );
  }
);

Index.displayName = 'select';

export default Index;
